import React, { useEffect, useRef } from "react";

import { useTranslation, I18nextProvider } from "react-i18next";
import instance from "../../../utils/i18n";

import { v4 as uuidv4 } from "uuid";

import {
  StyledDistanceFilter,
  StyledPopoverContainer,
  StyledPopoverButtons,
  StyledCFRow,
} from "./DistanceFilter-styled";
import { DistanceFilterProps } from "../Filter.types";

import "@esri/calcite-components/dist/components/calcite-button";
import "@esri/calcite-components/dist/components/calcite-popover";
import "@esri/calcite-components/dist/components/calcite-label";
import "@esri/calcite-components/dist/components/calcite-checkbox";
import "@esri/calcite-components/dist/components/calcite-radio-button";
import "@esri/calcite-components/dist/components/calcite-radio-button-group";
import {
  CalciteButton,
  CalcitePopover,
  CalciteLabel,
  CalciteRadioButton,
  CalciteRadioButtonGroup,
} from "@esri/calcite-components-react";

const DistanceFilter = ({
  options,
  selectedValue,
  unit,
  onChange,
  isFullscreen,
  filter_any_label,
}: DistanceFilterProps) => {
  const popoverRef = useRef<HTMLCalcitePopoverElement>(null);

  const { t, i18n } = useTranslation("components", { i18n: instance });

  const onClose = () => {
    if (popoverRef?.current) {
      popoverRef.current.open = !popoverRef.current.open;
    }
  };

  return (
    <I18nextProvider i18n={i18n}>
      {isFullscreen ? (
        <span>
          {options?.length > 0 &&
            options?.map((option) => (
              <StyledCFRow key={uuidv4()}>
                <CalciteLabel layout="inline" onClick={() => onChange(option)}>
                  {/* "|| undefined" below is workaround for issue with Calcite React where passing a boolean converts the value to a string */}
                  <CalciteRadioButton
                    value={option}
                    checked={option === selectedValue || undefined}
                  />
                  {`${option} ${t(`distanceFilter.units.${unit}`)}`}
                </CalciteLabel>
              </StyledCFRow>
            ))}
        </span>
      ) : (
        <StyledDistanceFilter>
          <CalciteButton
            id={`distance-filter-button-${filter_any_label.replace(/ /g, "_")}`}
            iconEnd="chevron-down"
            class={selectedValue ? "" : "lib-neutral-button"}
            appearance={selectedValue ? "solid" : "outline"}
            color={selectedValue ? "blue" : "neutral"}
            label={
              selectedValue
                ? `${selectedValue} ${t(`distanceFilter.units.${unit}`)}`
                : filter_any_label
            }
          >
            {selectedValue
              ? `${selectedValue} ${t(`distanceFilter.units.${unit}`)}`
              : filter_any_label}
          </CalciteButton>
          <CalcitePopover
            label={t("categoryFilter.selectTypes")}
            // using a ref for the referenceElement worked on first render, but once fullscreen mode was toggled the ref was not set on the next render
            // and caused the popover and button to get initialized improperly.  Hardcoded ids are less elegant but solve this problem.
            referenceElement={`distance-filter-button-${filter_any_label.replace(/ /g, "_")}`}
            auto-close
            id="distance-filter-popover"
            ref={popoverRef}
          >
            <StyledPopoverContainer>
              {options?.length > 0 && (
                <CalciteRadioButtonGroup name={uuidv4()} layout="vertical">
                  {options?.map((option) => (
                    <StyledCFRow key={uuidv4()}>
                      <CalciteLabel
                        layout="inline"
                        className="label-nowrap"
                        onClick={() => onChange(option)}
                      >
                        {/* "|| undefined" below is workaround for issue with Calcite React where passing a boolean converts the value to a string */}
                        <CalciteRadioButton
                          value={option}
                          checked={option === selectedValue || undefined}
                        />
                        {`${option} ${t(`distanceFilter.units.${unit}`)}`}
                      </CalciteLabel>
                    </StyledCFRow>
                  ))}
                </CalciteRadioButtonGroup>
              )}

              <StyledPopoverButtons>
                {selectedValue && (
                  <CalciteButton
                    class="lib-neutral-button"
                    color="neutral"
                    appearance="outline"
                    id="select-all-btn"
                    onClick={() => onChange(undefined)}
                  >
                    {t("distanceFilter.clear")}
                  </CalciteButton>
                )}
                {
                  <CalciteButton
                    id="close-distance-filter-btn"
                    onClick={onClose}
                    color="blue"
                    appearance="solid"
                  >
                    {t("distanceFilter.close")}
                  </CalciteButton>
                }
              </StyledPopoverButtons>
            </StyledPopoverContainer>
          </CalcitePopover>
        </StyledDistanceFilter>
      )}
    </I18nextProvider>
  );
};

export default DistanceFilter;
